import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Svg, Path } from 'react-native-svg';
import { useTranslation } from 'react-i18next';
import { FONTS } from '../constants/config';

import { tokens } from '@design';
import { getTextAlign } from '../utils/rtl';

interface TargetAchievementCardProps {
  title: string;
  achieved: number | string;
  target: number | string;
  unit?: string; // kept for API compatibility (no longer rendered)
  onViewDetails?: () => void;
  achievedColor?: string; // Drives progress bar + dot color (caller maps from status)
  /**
   * Engine % (matches donut). `null` = nothing due yet — renders a muted —
   * instead of a red 0%. `undefined` falls back to the achieved/target ratio.
   */
  percentage?: number | null;
}

const ChevronLeftIcon = ({ color }: { color: string }) => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path
      d="M10 12L6 8L10 4"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

export default function TargetAchievementCard({
  title,
  achieved,
  target,
  onViewDetails,
  achievedColor = '#2ca02c', // Green color from Figma
  percentage: percentageProp,
}: TargetAchievementCardProps) {
  const { t } = useTranslation();

  // Percentage: prefer the engine value (matches the donut); fall back to the
  // raw achieved/target ratio only when not supplied at all. Explicit null
  // means "nothing due yet" — muted state, never a red 0%.
  const noData = percentageProp === null;
  const actualNum = typeof achieved === 'number' ? achieved : parseFloat(String(achieved)) || 0;
  const targetNum = typeof target === 'number' ? target : parseFloat(String(target)) || 1;
  const ratioPct = Math.min(parseFloat(((actualNum / targetNum) * 100).toFixed(2)), 100);
  const percentage = percentageProp ?? ratioPct;
  const barWidth = noData ? 0 : Math.min(Math.max(percentage ?? 0, 0), 100);
  const barColor = noData ? tokens.colors.raw.gray["400"] : achievedColor;

  const displayValue = noData ? '—' : `% ${percentage}`;

  return (
    <View style={styles.card}>
      {/* KPI Title (wraps, never truncated) */}
      <Text style={styles.title}>{title}</Text>

      {/* Value badge + progress bar */}
      <View style={styles.valueRow}>
        <View style={styles.valueBadge}>
          <Text style={styles.value}>{displayValue}</Text>
        </View>

        <View style={styles.progressContainer}>
          <View style={styles.progressTrack}>
            <View
              style={[
                styles.progressFill,
                { width: `${barWidth}%`, backgroundColor: barColor },
              ]}
            />
            <View
              style={[
                styles.progressDot,
                { end: `${barWidth}%`, transform: [{ translateX: 7 }], backgroundColor: barColor },
              ]}
            />
          </View>
        </View>
      </View>

      {/* View Details link */}
      {onViewDetails && (
        <TouchableOpacity
          style={styles.viewDetailsButton}
          onPress={onViewDetails}
          activeOpacity={0.7}
        >
          <ChevronLeftIcon color={tokens.colors.semantic.general.foreground} />
          <Text style={styles.viewDetailsText}>
            {t('common.viewDetails') || 'عرض التفاصيل'}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  card: {
    width: '100%',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["3"],
  },
  title: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    lineHeight: 20,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
  },
  valueBadge: {
    backgroundColor: tokens.colors.raw.slate["100"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["0_5"],
    borderRadius: 100,
  },
  value: {
    fontFamily: FONTS.numbers,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontWeight: '500',
    color: tokens.colors.semantic.general.foreground,
  },
  progressContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  progressTrack: {
    height: 8,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    position: 'relative',
    overflow: 'visible',
  },
  progressFill: {
    position: 'absolute',
    end: 0,
    top: 0,
    height: '100%',
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  progressDot: {
    position: 'absolute',
    width: 14,
    height: 14,
    borderRadius: tokens.radii.absolute["radius-7"],
    top: -3,
    borderWidth: 2,
    borderColor: tokens.colors.raw.white,
    elevation: 2,
  },
  viewDetailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: 3,
    minHeight: 24,
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  viewDetailsText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    lineHeight: 13.57,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
  },
});

const styles = getStyles(true);
